// 3D-modus: Three.js scene met geëxtrudeerde block-polygonen op een PDOK-
// luchtfoto-grondvlak, met een directionele lichtbron volgens de zonpositie
// en real-time slagschaduw via shadowmap (zie §2.3 / §3.6 van het ontwerp).
//
// Lokaal coördinatenstelsel: oost = +x, noord = +z (Three.js' rechtshandige
// y-up conventie, dus het "scenario-vlak" is xz). Hoogtes (gebouw, bladdek)
// gaan langs +y.
//
// We rekenen blokken om naar lokale meters via dezelfde equirectangular
// benadering als in `shadow/projectShadow.ts`, gecentreerd op het scenario-
// centrum. Op onze schaal (≤500 m) is de fout verwaarloosbaar.

import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { REFERENCE_YEAR, useStore } from "../state/store";
import {
  dayOfYearToMonthDay,
  getSunPosition,
  nlClockToUtc,
  sunDirectionEnu,
} from "../shadow/sunPosition";
import type { Block } from "../types/scenario";
import { loadPdokGroundTexture } from "../three/pdokTexture";

const EARTH_R = 6_378_137;
const PDOK_ZOOM = 17;
const PDOK_GRID = 5; // 5×5 tegels rond het centrum

interface LocalProj {
  refLat: number;
  refLng: number;
  mPerLat: number;
  mPerLng: number;
}

function makeProjection(refLat: number, refLng: number): LocalProj {
  const mPerLat = (Math.PI * EARTH_R) / 180;
  const mPerLng = mPerLat * Math.cos((refLat * Math.PI) / 180);
  return { refLat, refLng, mPerLat, mPerLng };
}

/**
 * World-coördinaten conventie: oost = +x, omhoog = +y, noord = −z.
 * Dit zorgt dat de standaard-camera (kijkt langs −z) van nature naar het
 * noorden kijkt, en dat de zon-vector (ENU: y = noord) consistent omgezet
 * wordt naar three.js (worldZ = −ENU.y).
 */
function lngLatToWorld(p: [number, number], proj: LocalProj): { x: number; z: number } {
  return {
    x: (p[0] - proj.refLng) * proj.mPerLng,
    z: -(p[1] - proj.refLat) * proj.mPerLat,
  };
}

function buildBlockMesh(block: Block, proj: LocalProj): THREE.Mesh | null {
  if (block.heightTop <= 0) return null;
  // We bouwen de Shape in shape-coördinaten (XY-vlak). Na geo.rotateX(−π/2)
  // mapt shape-Y naar wereld-(−Z), dus we voeden shape-Y = −worldZ zodat
  // wereld-Z na rotatie correct uitkomt.
  const shape = new THREE.Shape();
  block.polygon.forEach((p, i) => {
    const w = lngLatToWorld(p, proj);
    const sx = w.x;
    const sy = -w.z;
    if (i === 0) shape.moveTo(sx, sy);
    else shape.lineTo(sx, sy);
  });
  const depth = block.heightTop - block.heightBottom;
  if (depth <= 0) return null;
  const geo = new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false });
  // ExtrudeGeometry extrudeert langs +z (binnen shape-frame). We willen de
  // extrusie omhoog (wereld +y). rotateX(−π/2): (X, Y, Z) → (X, Z, −Y),
  // dus shape-Y = sy = −worldZ → wereld-Z = −sy = worldZ. ✓
  geo.rotateX(-Math.PI / 2);
  // Plaats op de juiste hoogte-onderzijde.
  const transmission = (block.lightTransmission ?? 0) / 100;
  const opacity =
    block.type === "boombladdek" ? Math.max(0.4, 1 - transmission) : 1;
  const material = new THREE.MeshStandardMaterial({
    color: new THREE.Color(block.color || "#888888"),
    transparent: opacity < 1,
    opacity,
    roughness: 0.9,
    metalness: 0,
  });
  const mesh = new THREE.Mesh(geo, material);
  mesh.position.y = block.heightBottom;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  mesh.userData.blockId = block.id;
  return mesh;
}

export default function View3D() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // We bewaren scene-objecten in een ref-bag zodat re-renders van de React
  // wrapper de Three.js-staat niet opnieuw opbouwen.
  const sceneRef = useRef<{
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    controls: OrbitControls;
    sunLight: THREE.DirectionalLight;
    ambient: THREE.AmbientLight;
    blocksGroup: THREE.Group;
    proj: LocalProj;
    raf: number | null;
    disposed: boolean;
  } | null>(null);

  // ---- Mount ------------------------------------------------------------
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const view = useStore.getState().scenario.view;
    const proj = makeProjection(view.centerLat, view.centerLng);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#dde7ee");

    const camera = new THREE.PerspectiveCamera(
      55,
      container.clientWidth / Math.max(1, container.clientHeight),
      0.1,
      5000,
    );
    // Standaard kijkhoek: 30° boven horizon (§2.3). We plaatsen de camera op
    // afstand zodat ~150 m omtrekt zichtbaar is.
    const initialDist = 200;
    const pitchRad = (30 * Math.PI) / 180;
    camera.position.set(
      0,
      Math.sin(pitchRad) * initialDist,
      Math.cos(pitchRad) * initialDist,
    );
    camera.lookAt(0, 0, 0);

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: false,
      // Zonder preserveDrawingBuffer is renderer.domElement.toDataURL()
      // (de snapshot-export) leeg op de meeste browsers.
      preserveDrawingBuffer: true,
    });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(container.clientWidth, container.clientHeight);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    container.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 20;
    controls.maxDistance = 1500;
    controls.maxPolarAngle = Math.PI / 2 - 0.01; // niet onder horizon

    // Lichten
    const ambient = new THREE.AmbientLight(0xffffff, 0.45);
    scene.add(ambient);

    const sunLight = new THREE.DirectionalLight(0xffffff, 1.2);
    sunLight.castShadow = true;
    sunLight.shadow.mapSize.set(2048, 2048);
    // Shadow-camera bereik: ruim genoeg voor onze 500 m grid.
    const sm = sunLight.shadow.camera as THREE.OrthographicCamera;
    sm.left = -400;
    sm.right = 400;
    sm.top = 400;
    sm.bottom = -400;
    sm.near = 1;
    sm.far = 1500;
    sm.updateProjectionMatrix();
    scene.add(sunLight);
    scene.add(sunLight.target);

    // Hulpgrond (lichtgrijs) — wordt vervangen zodra de PDOK-textuur klaar is.
    const placeholderGround = new THREE.Mesh(
      new THREE.PlaneGeometry(800, 800),
      new THREE.MeshStandardMaterial({ color: "#cccccc", roughness: 1 }),
    );
    placeholderGround.rotation.x = -Math.PI / 2;
    placeholderGround.receiveShadow = true;
    placeholderGround.name = "ground";
    scene.add(placeholderGround);

    const blocksGroup = new THREE.Group();
    blocksGroup.name = "blocks";
    scene.add(blocksGroup);

    sceneRef.current = {
      renderer,
      scene,
      camera,
      controls,
      sunLight,
      ambient,
      blocksGroup,
      proj,
      raf: null,
      disposed: false,
    };
    _setActiveView3D({ renderer, scene, camera });

    // Initiële sync (lichten + blokken).
    syncSun();
    syncBlocks();

    // PDOK ground texture (asynchroon laden — we vervangen het placeholder-
    // ground zodra de textuur er is).
    loadPdokGroundTexture(view.centerLat, view.centerLng, PDOK_ZOOM, PDOK_GRID)
      .then((res) => {
        if (!sceneRef.current || sceneRef.current.disposed) return;
        const widthLng = res.bounds.east - res.bounds.west;
        const heightLat = res.bounds.north - res.bounds.south;
        const widthM = widthLng * proj.mPerLng;
        const heightM = heightLat * proj.mPerLat;
        const oldGround = sceneRef.current.scene.getObjectByName("ground");
        if (oldGround) {
          sceneRef.current.scene.remove(oldGround);
          (oldGround as THREE.Mesh).geometry.dispose();
          ((oldGround as THREE.Mesh).material as THREE.Material).dispose();
        }
        const ground = new THREE.Mesh(
          new THREE.PlaneGeometry(widthM, heightM),
          new THREE.MeshStandardMaterial({
            map: res.texture,
            roughness: 1,
            metalness: 0,
          }),
        );
        ground.rotation.x = -Math.PI / 2;
        ground.receiveShadow = true;
        ground.name = "ground";
        // Plaats het ground-mesh op het centrum van de tile-grid in
        // wereld-coördinaten (kan iets verschillen van het scenario-
        // centrum omdat de tile-grid altijd op tegelranden uitlijnt).
        const centerLng = (res.bounds.west + res.bounds.east) / 2;
        const centerLat = (res.bounds.north + res.bounds.south) / 2;
        const centerWorld = lngLatToWorld([centerLng, centerLat], proj);
        ground.position.set(centerWorld.x, 0, centerWorld.z);
        sceneRef.current.scene.add(ground);
      })
      .catch((err) => {
        console.warn("[View3D] PDOK textuur niet geladen:", err);
      });

    // Render-loop.
    const tick = () => {
      const s = sceneRef.current;
      if (!s || s.disposed) return;
      s.controls.update();
      s.renderer.render(s.scene, s.camera);
      s.raf = requestAnimationFrame(tick);
    };
    sceneRef.current.raf = requestAnimationFrame(tick);

    // Resize-handler.
    const onResize = () => {
      const s = sceneRef.current;
      if (!s || !container) return;
      const w = container.clientWidth;
      const h = Math.max(1, container.clientHeight);
      s.renderer.setSize(w, h);
      s.camera.aspect = w / h;
      s.camera.updateProjectionMatrix();
    };
    window.addEventListener("resize", onResize);
    const ro = new ResizeObserver(onResize);
    ro.observe(container);

    // Subscribe aan store-mutaties die de scene raken.
    const unsub = useStore.subscribe((s, prev) => {
      if (s.scenario.blocks !== prev.scenario.blocks) syncBlocks();
      if (
        s.currentDayOfYear !== prev.currentDayOfYear ||
        s.currentMinuteOfDay !== prev.currentMinuteOfDay
      ) {
        syncSun();
      }
    });

    return () => {
      const s = sceneRef.current;
      if (!s) return;
      s.disposed = true;
      if (s.raf !== null) cancelAnimationFrame(s.raf);
      window.removeEventListener("resize", onResize);
      ro.disconnect();
      unsub();
      s.controls.dispose();
      s.renderer.dispose();
      // Verwijder het canvas uit de DOM.
      if (s.renderer.domElement.parentElement === container) {
        container.removeChild(s.renderer.domElement);
      }
      // Geheugen vrijgeven van geometries en materialen in de scene.
      s.scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
          if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
          else obj.material.dispose();
        }
      });
      sceneRef.current = null;
      _setActiveView3D(null);
    };
  }, []);

  function syncBlocks(): void {
    const s = sceneRef.current;
    if (!s) return;
    // Verwijder oude meshes.
    while (s.blocksGroup.children.length) {
      const child = s.blocksGroup.children.pop()!;
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (Array.isArray(child.material)) child.material.forEach((m) => m.dispose());
        else child.material.dispose();
      }
    }
    const { scenario } = useStore.getState();
    for (const block of scenario.blocks) {
      const mesh = buildBlockMesh(block, s.proj);
      if (mesh) s.blocksGroup.add(mesh);
    }
  }

  function syncSun(): void {
    const s = sceneRef.current;
    if (!s) return;
    const state = useStore.getState();
    const { month, day } = dayOfYearToMonthDay(REFERENCE_YEAR, state.currentDayOfYear);
    const hour = Math.floor(state.currentMinuteOfDay / 60);
    const minute = state.currentMinuteOfDay % 60;
    const utc = nlClockToUtc(REFERENCE_YEAR, month, day, hour, minute);
    const sun = getSunPosition(utc, state.scenario.view.centerLat, state.scenario.view.centerLng);

    if (sun.altitude < 0) {
      // Zon onder horizon: dim de scene.
      s.sunLight.intensity = 0;
      s.ambient.intensity = 0.25;
      return;
    }
    s.sunLight.intensity = 1.2;
    s.ambient.intensity = 0.45;

    // ENU-vector → Three.js (oost = +x, noord = −z, op = +y).
    const dir = sunDirectionEnu(sun);
    // Zon staat op een grote afstand in de richting *waar de zon vandaan komt*.
    const D = 600;
    s.sunLight.position.set(dir.x * D, dir.z * D, -dir.y * D);
    s.sunLight.target.position.set(0, 0, 0);
    s.sunLight.target.updateMatrixWorld();
  }

  return <div ref={containerRef} className="w-full h-full" />;
}

// Module-level handles naar de actieve 3D-renderer voor de snapshot-knop.
// Worden bijgewerkt vanuit de mount-effect (zie sceneRef.current toewijzing
// hierboven; we bewaren extra refs op module-niveau zodat externe componenten
// niet via een React-context hoeven).
let activeView3D: {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
} | null = null;

export function getView3DCanvas(): HTMLCanvasElement | null {
  if (!activeView3D) return null;
  // Forceer een verse render: preserveDrawingBuffer levert pas een correcte
  // toDataURL als er net na de laatste render uitgelezen wordt.
  activeView3D.renderer.render(activeView3D.scene, activeView3D.camera);
  return activeView3D.renderer.domElement;
}

export function _setActiveView3D(
  v: {
    renderer: THREE.WebGLRenderer;
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
  } | null,
): void {
  activeView3D = v;
}

